import { browserTimeZone } from '../timeZones.ts';
import type { HttpClient } from '../http/HttpClient.ts';

export type OrgRole = 'owner' | 'admin' | 'member' | 'guest';
export type ProjectRole = 'admin' | 'member' | 'viewer';

export interface Org {
  id: string;
  name: string;
  slug: string;
  kind: 'personal' | 'team';
  /** Members must have two-factor on to open it. */
  requireTwoFactor: boolean;
  role: OrgRole;
  /** Paused over its owner's plan: listed, but it doesn't open. */
  paused: boolean;
  /** When it was paused, and whom to ask; null when not paused. */
  pause: { since: string; ownerName: string } | null;
}

/** An owner's offer to hand an organization to one of its members. */
export interface OwnershipOffer {
  org: { id: string; slug: string; name: string };
  from: { id: string; name: string };
  to: { id: string; name: string };
  expiresAt: string;
}

export interface OrgMember {
  userId: string;
  name: string;
  email: string;
  role: OrgRole;
  joinedAt: string;
  /** Whether they have two-factor on (owners and admins see it; null for others). */
  twoFactor: boolean | null;
  /** Paused over the owner's plan: can't open the organization. */
  paused: boolean;
}

/** One entry of an audit log (yours, or an organization's). */
export interface AuditEntry {
  id: string;
  action: string;
  /** Null when they've left the organization (a former member). */
  actor: { id: string; name: string } | null;
  ip: string | null;
  meta: Record<string, unknown>;
  createdAt: string;
}

export interface AuditPage {
  entries: AuditEntry[];
  next: string | null;
}

export interface Invite {
  id: string;
  email: string;
  orgRole: OrgRole;
  projectGrants: { projectId: string; role: ProjectRole }[];
  expiresAt: string;
  createdAt: string;
}

export interface InvitePreview {
  orgName: string;
  inviterName: string | null;
  email: string;
  accountExists: boolean;
}

const o = (slug: string) => `/orgs/${encodeURIComponent(slug)}`;

/** Organizations, their members and invitations. */
export class OrgsApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  list(): Promise<{ orgs: Org[] }> {
    return this.#http.get('/orgs');
  }

  create(name: string): Promise<{ org: Org }> {
    return this.#http.post('/orgs', { name });
  }

  get(slug: string): Promise<{ org: Org }> {
    return this.#http.get(o(slug));
  }

  rename(slug: string, name: string): Promise<{ org: Org }> {
    return this.#http.patch(o(slug), { name });
  }

  /** Require two-factor sign-in of everyone who opens it (owners and admins). */
  setTwoFactorRule(slug: string, requireTwoFactor: boolean): Promise<{ org: Org }> {
    return this.#http.patch(`${o(slug)}/security`, { requireTwoFactor });
  }

  /** The organization's audit log, newest first (owners and admins). */
  auditLog(slug: string, after: string | null): Promise<AuditPage> {
    const query = after ? `?after=${encodeURIComponent(after)}` : '';
    return this.#http.get(`${o(slug)}/audit-log${query}`);
  }

  /** Bytes stored across the organization's projects (owners and admins). */
  storage(slug: string): Promise<{ storage: { bytes: number } }> {
    return this.#http.get(`/orgs/${slug}/storage`);
  }

  members(slug: string): Promise<{ members: OrgMember[] }> {
    return this.#http.get(`${o(slug)}/members`);
  }

  changeRole(slug: string, userId: string, role: Exclude<OrgRole, 'owner'>): Promise<void> {
    return this.#http.patch(`${o(slug)}/members/${userId}`, { role });
  }

  removeMember(slug: string, userId: string): Promise<void> {
    return this.#http.delete(`${o(slug)}/members/${userId}`);
  }

  leave(slug: string): Promise<void> {
    return this.#http.post(`${o(slug)}/leave`);
  }

  invites(slug: string): Promise<{ invites: Invite[] }> {
    return this.#http.get(`${o(slug)}/invites`);
  }

  invite(
    slug: string,
    input: {
      email: string;
      orgRole: Exclude<OrgRole, 'owner'>;
      projectGrants: { projectId: string; role: ProjectRole }[];
    },
  ): Promise<{ invite: Invite; emailed: boolean }> {
    return this.#http.post(`${o(slug)}/invites`, input);
  }

  revokeInvite(slug: string, id: string): Promise<void> {
    return this.#http.delete(`${o(slug)}/invites/${id}`);
  }

  /** Whether it was emailed (an address that bounced or complained isn't). */
  resendInvite(slug: string, id: string): Promise<{ emailed: boolean }> {
    return this.#http.post(`${o(slug)}/invites/${id}/resend`);
  }

  previewInvite(token: string): Promise<{ invite: InvitePreview }> {
    return this.#http.post('/invites/preview', { token });
  }

  acceptInvite(token: string): Promise<{ orgSlug: string }> {
    return this.#http.post('/invites/accept', { token });
  }

  acceptInviteAsNewUser(input: {
    token: string;
    name: string;
    password: string;
  }): Promise<{ orgSlug: string }> {
    const timeZone = browserTimeZone();
    return this.#http.post('/invites/accept-new', timeZone ? { ...input, timeZone } : input);
  }

  /** The open ownership offer (owners and admins see it), or null. */
  ownershipOffer(slug: string): Promise<{ offer: OwnershipOffer | null }> {
    return this.#http.get(`${o(slug)}/ownership-offer`);
  }

  offerOwnership(slug: string, userId: string): Promise<{ offer: OwnershipOffer }> {
    return this.#http.post(`${o(slug)}/ownership-offer`, { userId });
  }

  withdrawOwnershipOffer(slug: string): Promise<void> {
    return this.#http.delete(`${o(slug)}/ownership-offer`);
  }

  /** Offers waiting for you. */
  myOwnershipOffers(): Promise<{ offers: OwnershipOffer[] }> {
    return this.#http.get('/me/ownership-offers');
  }

  acceptOwnership(orgId: string): Promise<void> {
    return this.#http.post(`/me/ownership-offers/${encodeURIComponent(orgId)}/accept`);
  }

  declineOwnership(orgId: string): Promise<void> {
    return this.#http.post(`/me/ownership-offers/${encodeURIComponent(orgId)}/decline`);
  }
}
